import { useState } from "react";
import { Button, Flex, IconButton, Text } from "@radix-ui/themes";
import { PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { SelectOption } from "@/components/shared/select/Select";
import { UpsertSampleConfigurationParams } from "@/v2/entities/sampling/Sampling.api";
import { RuleBuilder } from "./RuleBuilder";
import styles from "./SampleForm.module.css";
import { Sources } from "@/v2/entities/tracker/Tracker.api";

const numberOfTicketsOptions: SelectOption[] = [
  { label: "25", value: "25" },
  { label: "50", value: "50" },
  { label: "100", value: "100" },
  { label: "200", value: "200" },
];

const sourcesOptions: SelectOption[] = [
  { label: "Tickets", value: "tickets" },
  { label: "Conversations", value: "conversations" },
];

const daysOptions: SelectOption[] = [
  { label: "Daily", value: "1" },
  { label: "Weekly", value: "7" },
  { label: "Bi-weekly", value: "14" },
  { label: "Monthly", value: "30" },
];

interface SampleConfigurationProps {
  value: UpsertSampleConfigurationParams;
  onChange: (config: UpsertSampleConfigurationParams) => void;
  viewOnly?: boolean;
}

export function SampleConfiguration({
  value,
  onChange,
  viewOnly = false,
}: SampleConfigurationProps) {
  const [showBuilder, setShowBuilder] = useState(value.qaRules !== null);

  return (
    <div className="p-4 border border-gray-200 rounded-lg shadow-sm flex flex-col gap-4">
      <Flex gap={"2"} justify={"between"}>
      
        <Flex
          direction={"column"}
          gap={"2"}
          className={styles.sampleConfiguration}
        >
          <Text size={"3"} color={"gray"}>
            A validation set of
            <DropdownMenu
              items={numberOfTicketsOptions.map((opt) => ({
                label: opt.label,
                onClick: () =>
                  onChange({
                    ...value,
                    numberOfTickets: Number(opt.value),
                  }),
              }))}
            >
              <DropdownMenu.Trigger>
                <Button
                  variant="soft"
                  color="gray"
                  highContrast
                  style={{
                    marginTop: -4,
                    marginLeft: 4,
                    marginRight: 4,
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: "pointer",
                    pointerEvents: viewOnly ? "none" : "auto",
                  }}
                >
                  {value.numberOfTickets}
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
            <DropdownMenu
              items={sourcesOptions.map((opt) => ({
                label: opt.label,
                onClick: () =>
                  onChange({
                    ...value,
                    sources: [opt.value as Sources],
                  }),
              }))}
            >
              <DropdownMenu.Trigger>
                <Button
                  variant="soft"
                  color="gray"
                  highContrast
                  style={{
                    marginTop: -4,
                    marginLeft: 4,
                    marginRight: 4,
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: "pointer",
                    pointerEvents: viewOnly ? "none" : "auto",
                  }}
                >
                  {
                    sourcesOptions.find((opt) => opt.value === value.sources?.[0])
                      ?.label
                  }
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
            will be generated on a
            <DropdownMenu
              items={daysOptions.map((opt) => ({
                label: opt.label,
                onClick: () =>
                  onChange({
                    ...value,
                    daysUntilNextRun: Number(opt.value),
                  }),
              }))}
            >
              <DropdownMenu.Trigger>
                <Button
                  variant="soft"
                  color="gray"
                  highContrast
                  style={{
                    marginTop: -4,
                    marginLeft: 4,
                    marginRight: 4,
                    fontSize: 16,
                    fontWeight: 500,
                    cursor: "pointer",
                    pointerEvents: viewOnly ? "none" : "auto",
                  }}
                >
                  {
                    daysOptions.find(
                      (opt) => opt.value === value.daysUntilNextRun?.toString()
                    )?.label
                  }
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
            basis.
          </Text>

          {!showBuilder && (
            <Flex>
              <Button
                variant="ghost"
                color="gray"
                highContrast
                style={{
                  marginTop: -4,
                  marginLeft: -8,
                  height: "24px",
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                onClick={() => setShowBuilder(true)}
              >
                <PlusIcon />
                Add Advance Logic
              </Button>
            </Flex>
          )}

          {showBuilder && (
            <Flex pt={"2"} direction={"column"} gap={"2"}>
              <Text size={"2"} color={"gray"} weight={"medium"} highContrast>
                {!viewOnly ? "Select" : ""} Sampling Rules
              </Text>

              <RuleBuilder
                value={value.qaRules ?? null}
                viewOnly={viewOnly}
                onChange={(newQaRules) =>
                  onChange({
                    ...value,
                    qaRules: newQaRules,
                  })
                }
              />
            </Flex>
          )}
        </Flex>

        {!viewOnly && (
              <Flex gap={"4"}>

                <IconButton
                  variant="ghost"
                  color="red"
                  size="2"
                  highContrast
                  onClick={() => onChange({ ...value, isEnabled: false })}
                >
                  <TrashIcon />
                </IconButton>
              </Flex>
            )}
      </Flex>
    </div>
  );
}
